import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Modal, Button, Select } from '../../../components';
import { qualityService } from '../../../services';
import { FieldLabel, Notice, inputClass, isoPlusDays, todayISO, errorMessage } from './shared';

/**
 * Schedule an audit, or correct the details of one already running.
 *
 * The two modes share a form because they are the same decisions; what differs
 * is that the checklist is only chosen once. On edit the checklist is not
 * shown at all — answers already recorded belong to those questions, and
 * swapping the questions underneath them would leave findings attached to
 * text nobody answered.
 *
 * The templates are starting points, and the copy says so. They arrive from
 * the API (data/qualityTemplates.js) rather than living here, so the checks a
 * manager sees are the same ones the server knows about.
 */
const AuditFormModal = ({ open, onClose, meta, audit, onSaved }) => {
  const editing = Boolean(audit);
  const templates = meta?.templates || {};

  const [templateKey, setTemplateKey] = useState('medicines');
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Rebuilt each time the dialog opens so a cancelled edit never leaks into
  // the next one.
  useEffect(() => {
    if (!open) return;
    setError('');
    if (editing) {
      setForm({
        title: audit.title || '',
        area: audit.area || '',
        owner_id: audit.owner_id ? String(audit.owner_id) : '',
        due_date: String(audit.due_date || '').slice(0, 10),
        repeat_cycle: audit.repeat_cycle || 'One-off',
        policy_id: audit.policy_id ? String(audit.policy_id) : '',
        questions: '',
      });
      return;
    }
    const first = templates.medicines || Object.values(templates)[0] || { title: '', area: '', questions: [] };
    setTemplateKey('medicines');
    setForm({
      title: first.title || '',
      area: first.area || 'Quality & governance',
      owner_id: meta?.staff?.[0]?.id ? String(meta.staff[0].id) : '',
      due_date: isoPlusDays(7),
      repeat_cycle: 'One-off',
      policy_id: matchPolicy(meta, first.policyHint),
      questions: (first.questions || []).join('\n'),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, audit?.id]);

  const staffOptions = useMemo(
    () => (meta?.staff || []).map((s) => ({ value: String(s.id), label: s.name })),
    [meta],
  );
  const policyOptions = useMemo(
    () => [
      { value: '', label: 'No linked policy' },
      ...(meta?.policies || []).map((p) => ({
        value: String(p.id),
        label: p.version ? `${p.title} · v${p.version}` : p.title,
      })),
    ],
    [meta],
  );

  if (!open || !form) return null;

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  // Choosing a template replaces the name, area, checklist and suggested
  // policy together — they are one starting point, not four settings.
  const applyTemplate = (key) => {
    setTemplateKey(key);
    const template = templates[key];
    if (!template) return;
    setForm((prev) => ({
      ...prev,
      title: template.title || '',
      area: template.area || prev.area,
      questions: (template.questions || []).join('\n'),
      policy_id: matchPolicy(meta, template.policyHint),
    }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setError('');

    const questions = form.questions.split('\n').map((line) => line.trim()).filter(Boolean);
    if (!editing && !questions.length) {
      setError('Add at least one checklist item.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        area: form.area,
        owner_id: form.owner_id ? Number(form.owner_id) : null,
        due_date: form.due_date,
        repeat_cycle: form.repeat_cycle,
        policy_id: form.policy_id ? Number(form.policy_id) : null,
      };
      const response = editing
        ? await qualityService.updateAudit(audit.id, payload)
        : await qualityService.createAudit({ ...payload, questions });

      toast.success(response.message || 'Saved.');
      onSaved(response.data);
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={editing ? 'Edit audit details' : 'Schedule an audit'}
      maxWidth="max-w-2xl"
    >
      <form onSubmit={submit} className="space-y-4">
        <p className="text-[12.5px] text-[#64748B] dark:text-gray-400 -mt-1">
          {editing
            ? 'Update the assignment and due date. Checklist answers stay with this audit.'
            : 'Choose a checklist, assign an owner and set a due date.'}
        </p>

        {error && <Notice tone="red">{error}</Notice>}

        {!editing && (
          <div>
            <FieldLabel hint="Starter checks are examples — adapt them to your service">Checklist</FieldLabel>
            <Select
              value={templateKey}
              onChange={applyTemplate}
              options={Object.entries(templates).map(([key, t]) => ({
                value: key,
                label: key === 'custom' ? 'Create my own checklist' : t.title,
              }))}
            />
          </div>
        )}

        <div>
          <FieldLabel required>Audit name</FieldLabel>
          <input
            className={inputClass}
            value={form.title}
            onChange={(e) => set('title', e.target.value)}
            maxLength={150}
            required
            placeholder="e.g. Medicines management audit"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <FieldLabel required>Audit owner</FieldLabel>
            <Select
              value={form.owner_id}
              onChange={(v) => set('owner_id', v)}
              options={staffOptions}
              placeholder="Choose a staff member"
            />
          </div>
          <div>
            <FieldLabel required>Due date</FieldLabel>
            <input
              type="date"
              className={inputClass}
              value={form.due_date}
              onChange={(e) => set('due_date', e.target.value)}
              // A new audit cannot be due in the past; an edit may legitimately
              // move a date back to correct a mistake.
              min={editing ? undefined : todayISO()}
              required
            />
          </div>
          <div>
            <FieldLabel>Area</FieldLabel>
            <Select value={form.area} onChange={(v) => set('area', v)} options={meta?.areas || []} />
          </div>
          <div>
            <FieldLabel hint="A repeat is scheduled when you sign this off">Repeat</FieldLabel>
            <Select
              value={form.repeat_cycle}
              onChange={(v) => set('repeat_cycle', v)}
              options={meta?.repeatCycles || ['One-off']}
            />
          </div>
        </div>

        <div>
          <FieldLabel hint="Optional">Linked policy</FieldLabel>
          <Select value={form.policy_id} onChange={(v) => set('policy_id', v)} options={policyOptions} />
          <p className="text-[11px] text-[#94A3B8] dark:text-gray-500 mt-1.5">
            The version in force today is recorded with the audit, so what you checked against stays clear
            after the policy is next republished.
          </p>
        </div>

        {!editing && (
          <div>
            <FieldLabel required hint="One check per line">Checklist items</FieldLabel>
            <textarea
              className={`${inputClass} font-mono text-[12.5px] leading-relaxed`}
              rows={7}
              value={form.questions}
              onChange={(e) => set('questions', e.target.value)}
              placeholder="Write one checklist item per line…"
            />
            <p className="text-[11px] text-[#94A3B8] dark:text-gray-500 mt-1.5">
              Edit these before creating the audit. You can add more checks while you work.
            </p>
          </div>
        )}

        <div className="flex items-center justify-end gap-2.5 pt-2">
          <Button variant="secondary" onClick={onClose} type="button">Cancel</Button>
          <Button variant="primary" type="submit" loading={saving}>
            {editing ? 'Save changes' : 'Create audit'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

/**
 * Pre-select the policy a template is about, when the organisation happens to
 * have one by that name. A hint that matches nothing simply leaves the link
 * empty rather than guessing at a near-match.
 */
const matchPolicy = (meta, hint) => {
  if (!hint) return '';
  const found = (meta?.policies || []).find(
    (p) => String(p.title || '').trim().toLowerCase() === String(hint).trim().toLowerCase(),
  );
  return found ? String(found.id) : '';
};

export default AuditFormModal;
